/**
 * The multicam studio (/annotate-multicam/:multicamSetId).
 *
 * Built from the annotation studio's parts: the same player and transport
 * (with an angle switcher in the transport bar), the same timeline (with an
 * angles lane showing which angle the cuts show over the program), the same
 * notes list and shared categories, all frame-accurate on the set's shared
 * timeline.
 *
 * Two views (sets with two or more angles): Review, one angle at a time with
 * the composer, and Live cut, every angle at once, cut in real time with the
 * number keys or by tapping an angle (see MulticamLiveSwitcher and
 * liveCutSession). Switching keeps the playhead and pauses.
 *
 * Saving is unchanged from the previous annotator: each change is its own
 * mutation, sent when it is made (add a note or a cut, save an edit, drop a
 * marker after dragging it, delete). There is no batch save to wait for.
 * Every mutation goes through one queue, so the server sees changes in the
 * order they were made.
 */

import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Skeleton,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import { useAuth } from '../../context/AuthContext';
import {
  MulticamAngle,
  MulticamAnnotation,
  MulticamSet,
  addMulticamAnnotation,
  deleteMulticamAnnotation,
  fetchMulticamAnnotations,
  fetchMulticamSet,
  setMulticamStatus,
  updateMulticamAnnotation,
} from '../../graphqlService';
import { brand } from '../../theme';
import { directionCategoryOf } from '../../utils/annotationCategories';
import { pluralize } from '../../utils/format';
import { DEFAULT_FPS, formatTimecode } from '../../utils/timecode';
import EmptyState from '../ui/EmptyState';
import LoadingState from '../ui/LoadingState';
import ProTimeline from '../Timeline/ProTimeline';
import type { ProTimelineHandle, TimelineItem, TimelineItemChange, TimelineLane } from '../Timeline/timelineTypes';
import TransportBar from '../AnnotationStudio/TransportBar';
import VideoStage, { useFullscreen } from '../AnnotationStudio/VideoStage';
import AnnotationPanel from '../AnnotationStudio/AnnotationPanel';
import FrameRateChip from '../AnnotationStudio/FrameRateChip';
import SaveStatus from '../AnnotationStudio/SaveStatus';
import ShortcutHelpDialog from '../AnnotationStudio/ShortcutHelpDialog';
import {
  StudioAnnotation,
  adjacentAnnotationTime,
  annotationsActiveAt,
  resolveNoteSpan,
} from '../AnnotationStudio/annotationModel';
import AngleSwitcher from './AngleSwitcher';
import DeleteAnnotationDialog from './DeleteAnnotationDialog';
import MulticamAnnotationDialog from './MulticamAnnotationDialog';
import MulticamComposer, { ComposerMode } from './MulticamComposer';
import MulticamLiveSwitcher, { LiveSwitcherHandle, undoShortcutLabel } from './MulticamLiveSwitcher';
import { RECORDING_KEY, recordingAngle, recordingDisplayName, recordingPhrase, transcriptSourceOf } from './separateAudio';
import MulticamStudioHeader, { MULTICAM_LIBRARY_PATH } from './MulticamStudioHeader';
import ViewerBadge from './ViewerBadge';
import NotesTranscriptSwitch from '../Transcript/NotesTranscriptSwitch';
import TranscriptPanel, { TranscriptLineAction } from '../Transcript/TranscriptPanel';
import { markDeletedMessage } from '../Transcript/useTranscriptMarks';
import { LiveCutContext, LiveCutSession, createLiveCutSession } from './liveCutSession';
import SuggestCutsPanel from './SuggestCutsPanel';
import { useSpeakingActivity } from './useSpeakingActivity';
import { ANNOTATION_PAGE_SIZE, fetchRestOfMulticamAnnotations } from '../../services/speakersApi';
import { useMulticamPlayer } from './useMulticamPlayer';
import { useMulticamTranscriptMarks } from './useMulticamTranscriptMarks';
import { MULTICAM_SHORTCUTS, isTypingTarget } from './multicamShortcuts';
import { MulticamStudioGuide } from './MulticamHowItWorks';
import { annotationIdOf, buildMulticamLanes, buildScrubMarkers, buildStageNotes } from './multicamTimeline';
import { playsInSequence } from './multicamSequence';
import {
  ALL_ANGLES,
  AnnotationDraft,
  LOAD_ERROR_COPY,
  LoadProblem,
  SaveAction,
  angleName,
  classifyLoadError,
  cutAtFrame,
  cutLabel,
  defaultCutTarget,
  describeSaveError,
  draftFromAnnotation,
  findAngle,
  isCut,
  liveAngleKeyAt,
  masterAngleKey,
  msFromSeconds,
  normalizeAnnotations,
  normalizeMulticamSet,
  sortAnnotations,
  toMutationInput,
  toPanelAnnotation,
} from './multicamStudioModel';
import { angleColor, playerTokens } from './multicamTheme';

/** Review: one angle on screen, with the composer. Live cut: every angle, cut as it plays. */
type StudioView = 'review' | 'live';

const FPS_STORAGE_PREFIX = 'pithplay.multicam.fps.';
const EMPTY_ANGLES: MulticamAngle[] = [];

const readStoredFps = (setId?: string): number | null => {
  if (!setId) return null;
  try {
    const value = Number(window.localStorage.getItem(`${FPS_STORAGE_PREFIX}${setId}`));
    return Number.isFinite(value) && value > 0 ? value : null;
  } catch {
    return null;
  }
};

const storeFps = (setId: string | undefined, fps: number) => {
  if (!setId) return;
  try {
    window.localStorage.setItem(`${FPS_STORAGE_PREFIX}${setId}`, String(fps));
  } catch {
    /* storage unavailable: the choice lasts for this visit only */
  }
};

const VIEW_STORAGE_PREFIX = 'pithplay.multicam.view.';

/** The view the creator last chose for this set, if any. */
const readStoredView = (setId?: string): StudioView | null => {
  if (!setId) return null;
  try {
    const value = window.localStorage.getItem(`${VIEW_STORAGE_PREFIX}${setId}`);
    return value === 'review' || value === 'live' ? value : null;
  } catch {
    return null;
  }
};

const storeView = (setId: string | undefined, view: StudioView) => {
  if (!setId) return;
  try {
    window.localStorage.setItem(`${VIEW_STORAGE_PREFIX}${setId}`, view);
  } catch {
    /* storage unavailable: the choice lasts for this visit only */
  }
};

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'info' | 'warning';
  /** Puts back what was just changed. */
  undo?: () => void;
}

type LoadStatus = 'loading' | 'ready' | 'error';

/** The number keys that cut to each angle in live cut, in words: "1 or 2", "1, 2 or 3". */
const liveKeysText = (angleCount: number): string => {
  const keys = Array.from({ length: Math.max(1, Math.min(angleCount, 9)) }, (_, index) => String(index + 1));
  return keys.length === 1 ? keys[0] : `${keys.slice(0, -1).join(', ')} or ${keys[keys.length - 1]}`;
};

const STILL_SAVING = 'This cut is still saving. Try again in a moment.';

/** Keep keyboard focus where it was, so Space keeps controlling playback. */
const keepFocus = (event: React.MouseEvent) => event.preventDefault();

/** "1 or 2", "1, 2 or 3", "1–5": the number keys that cut to the set's angles. */
const cutKeysLabel = (count: number) => {
  const keys = Math.min(9, count);
  if (keys <= 1) return '1';
  if (keys === 2) return '1 or 2';
  if (keys === 3) return '1, 2 or 3';
  return `1–${keys}`;
};

const AnnotateMulticam: React.FC = () => {
  const { multicamSetId } = useParams<{ multicamSetId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const userId = user?.uid ?? null;

  // ---- The set and its annotations ---------------------------------------
  const [multicamSet, setMulticamSet] = useState<MulticamSet | null>(null);
  const [setStatus, setSetStatus] = useState<LoadStatus>('loading');
  const [loadProblem, setLoadProblem] = useState<LoadProblem>('unknown');
  const [setNonce, setSetNonce] = useState(0);

  const [annotations, setAnnotations] = useState<MulticamAnnotation[]>([]);
  const annotationsRef = useRef(annotations);
  annotationsRef.current = annotations;
  const [notesStatus, setNotesStatus] = useState<LoadStatus>('loading');
  const [notesNonce, setNotesNonce] = useState(0);

  // Keyed on the user's id, not the user object: a token refresh must not
  // reload the page over someone's work.
  useEffect(() => {
    if (!multicamSetId || !userId) return undefined;
    let cancelled = false;
    setSetStatus('loading');
    (async () => {
      try {
        const set = await fetchMulticamSet(multicamSetId);
        if (cancelled) return;
        if (!set) {
          setLoadProblem('not-found');
          setSetStatus('error');
          return;
        }
        setMulticamSet(normalizeMulticamSet(set));
        setSetStatus('ready');
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load multicam set:', err);
        setLoadProblem(classifyLoadError(err));
        setSetStatus('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [multicamSetId, userId, setNonce]);

  useEffect(() => {
    if (!multicamSetId || !userId || setStatus !== 'ready') return undefined;
    let cancelled = false;
    setNotesStatus('loading');
    const show = (list: MulticamAnnotation[]) => {
      if (cancelled) return;
      setAnnotations(normalizeAnnotations(list));
      setNotesStatus('ready');
    };
    fetchMulticamAnnotations(multicamSetId, ANNOTATION_PAGE_SIZE, 0)
      .then((list) => (Array.isArray(list) && list.length >= ANNOTATION_PAGE_SIZE && !cancelled
        // More than a page (hundreds of suggested cuts): load the rest before showing any.
        ? fetchRestOfMulticamAnnotations(multicamSetId, list).then(show)
        : show(list)))
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load multicam annotations:', err);
        setNotesStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [multicamSetId, userId, setStatus, notesNonce]);

  // ---- Frame rate (not recorded for multicam angles; chosen per set) -----
  const [fpsOverride, setFpsOverride] = useState<number | null>(() => readStoredFps(multicamSetId));
  const fps = fpsOverride ?? DEFAULT_FPS;

  // ---- Composer, marks and selection ---------------------------------------
  const [mode, setMode] = useState<ComposerMode>('note');
  const [composerText, setComposerText] = useState('');
  const [composerCategory, setComposerCategory] = useState('general');
  /** Which angle a new note is about; null follows the angle on screen. */
  const [noteScope, setNoteScope] = useState<string | null>(null);
  /** The angle a new cut goes to; null picks one (see defaultCutTarget). */
  const [cutTarget, setCutTarget] = useState<string | null>(null);
  const [cutReason, setCutReason] = useState('');
  const [inPoint, setInPoint] = useState<number | null>(null);
  const [outPoint, setOutPoint] = useState<number | null>(null);
  const [loopEnabled, setLoopEnabled] = useState(false);
  const [adding, setAdding] = useState(false);
  const addingRef = useRef(false);
  const noteInputRef = useRef<HTMLTextAreaElement | null>(null);
  const cutInputRef = useRef<HTMLInputElement | null>(null);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);

  // ---- Review or live cut ---------------------------------------------------
  // A set of two or more angles opens on live cut, every angle on screen,
  // until the creator picks a view for it; that choice is kept for the set.
  const [chosenView, setChosenView] = useState<StudioView | null>(() => readStoredView(multicamSetId));
  const view: StudioView = chosenView ?? 'live';
  const liveRef = useRef<LiveSwitcherHandle | null>(null);
  /** Where the live view opens: the review player's playhead when it was chosen. */
  const [liveStart, setLiveStart] = useState(0);
  /** The live view's clock (throttled while playing), for the timeline and the notes list. */
  const [liveClock, setLiveClock] = useState({ time: 0, playing: false });

  // ---- Save status (every change is its own request) ----------------------
  const [savesInFlight, setSavesInFlight] = useState(0);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  /** Latest request per annotation, so a slow older answer never wins. */
  const requestSeqRef = useRef<Record<string, number>>({});

  const trackSave = useCallback(async <T,>(work: () => Promise<T>): Promise<T> => {
    setSavesInFlight((count) => count + 1);
    try {
      const result = await work();
      setLastSavedAt(new Date());
      return result;
    } finally {
      setSavesInFlight((count) => Math.max(0, count - 1));
    }
  }, []);

  /**
   * One queue for every annotation request: each starts when the one before
   * it has finished, so the server sees changes in the order they were made
   * (a live cut's delete never overtakes the add it depends on, and an edit
   * never crosses a live change to the same cut). A request counts as saving
   * from the moment it is queued.
   */
  const saveChainRef = useRef<Promise<unknown>>(Promise.resolve());
  const runInOrder = useCallback(<T,>(work: () => Promise<T>): Promise<T> => {
    const run = saveChainRef.current.then(() => work());
    saveChainRef.current = run.then(() => undefined, () => undefined);
    return trackSave(() => run);
  }, [trackSave]);

  // A request is still on its way: warn before the tab closes.
  useEffect(() => {
    if (savesInFlight === 0) return undefined;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [savesInFlight]);

  const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false, message: '', severity: 'success' });
  const notify = useCallback((message: string, severity: SnackbarState['severity'] = 'success', undo?: () => void) => {
    setSnackbar({ open: true, message, severity, undo });
  }, []);

  // ---- Player ----------------------------------------------------------------
  const angles = multicamSet?.angles ?? EMPTY_ANGLES;
  const masterKey = useMemo(() => masterAngleKey(angles), [angles]);
  // A separate sound recording is heard (and transcribed) instead of any angle.
  const separateAudioSource = multicamSet?.audioSource;
  const recording = useMemo(() => recordingAngle(separateAudioSource), [separateAudioSource]);
  const player = useMulticamPlayer({
    angles,
    audioAngleKey: multicamSet?.audioAngleKey,
    audioSource: recording,
    initialAngleKey: masterKey,
    fps,
    loop: { enabled: loopEnabled, start: inPoint, end: outPoint },
  });
  const { seek, pause, getTime, selectAngle: selectPlayerAngle } = player;
  // Who speaks when (suggested cuts, names in the transcript), and the suggestions' preview lane.
  const speakers = useSpeakingActivity(multicamSet?.speakingActivity, angles);
  const [suggestionLane, setSuggestionLane] = useState<TimelineLane | null>(null);
  const fullscreen = useFullscreen<HTMLDivElement>();
  const timelineRef = useRef<ProTimelineHandle | null>(null);
  const currentTime = player.currentTime;
  const duration = player.duration;
  const viewKey = player.viewKey;
  const viewAngle = player.viewAngle;
  const audioAngle = player.audioAngle;

  const locked = multicamSet?.status === 'LOCKED';
  const editable = setStatus === 'ready' && notesStatus === 'ready' && !locked;

  // A new angle on screen: pick the cut target afresh.
  useEffect(() => {
    setCutTarget(null);
  }, [viewKey]);

  const angleKeys = useMemo(() => angles.map((angle) => angle.angleKey), [angles]);

  // ---- Live cut: takes, saving in order, rollback and undo -------------------
  /** Applies a change to the annotations at once, so quick presses plan on each other. */
  const updateAnnotations = useCallback((change: (list: MulticamAnnotation[]) => MulticamAnnotation[]) => {
    annotationsRef.current = change(annotationsRef.current);
    setAnnotations((previous) => change(previous));
  }, []);
  const liveContextRef = useRef<LiveCutContext>({ multicamSetId, fps, masterKey, angleKeys, editable });
  liveContextRef.current = { multicamSetId, fps, masterKey, angleKeys, editable };
  const [, liveChanged] = useReducer((count: number) => count + 1, 0);
  const liveSessionRef = useRef<LiveCutSession | null>(null);
  if (!liveSessionRef.current) {
    liveSessionRef.current = createLiveCutSession({
      getAnnotations: () => annotationsRef.current,
      updateAnnotations,
      runInOrder,
      api: {
        add: (setId, input) => addMulticamAnnotation(setId, input),
        update: (id, input) => updateMulticamAnnotation(id, input),
        remove: (id) => deleteMulticamAnnotation(id),
      },
      getContext: () => liveContextRef.current,
      onSaveError: (error, action) => {
        console.error('Failed to save a live cut:', error);
        notify(describeSaveError(error, action), 'error');
      },
      onChange: liveChanged,
    });
  }
  const liveSession = liveSessionRef.current;

  const liveMode = view === 'live' && angles.length > 1;
  /** The playhead of the view in use. */
  const playheadTime = liveMode ? liveClock.time : currentTime;
  const isPlaying = liveMode ? liveClock.playing : player.playing;

  // ---- Derived views of the annotations -------------------------------------
  const liveKey = useMemo(
    () => liveAngleKeyAt(annotations, playheadTime, masterKey, fps),
    [annotations, playheadTime, masterKey, fps]
  );
  const panelAnnotations = useMemo<StudioAnnotation[]>(
    () => annotations.map((annotation) => toPanelAnnotation(annotation, angles)),
    [annotations, angles]
  );
  const annotationById = useMemo(() => new Map(annotations.map((annotation) => [annotation.id, annotation])), [annotations]);

  const activeKey = useMemo(
    () => annotationsActiveAt(panelAnnotations, playheadTime).map((annotation) => annotation.id).join('|'),
    [panelAnnotations, playheadTime]
  );
  const activeIds = useMemo(() => (activeKey ? activeKey.split('|') : []), [activeKey]);

  const stageNotes = useMemo(
    () => buildStageNotes(annotations, activeIds, angles, fps),
    [annotations, activeIds, angles, fps]
  );
  const scrubMarkers = useMemo(() => buildScrubMarkers(annotations), [annotations]);

  const { hasFootage } = player;
  const measuredAngles = player.angles;
  const lanes = useMemo(
    () => buildMulticamLanes({ annotations, angles: measuredAngles, masterKey, duration, editable, hasFootage }),
    [annotations, duration, editable, hasFootage, masterKey, measuredAngles]
  );

  // ---- The cut composer's target and whether a cut can go here ----------------
  const footageNow = (key: string) => hasFootage(key, currentTime);
  const autoCutTarget = defaultCutTarget(angleKeys, viewKey, liveKey, footageNow);
  const cutTargetKey = cutTarget && angleKeys.includes(cutTarget) ? cutTarget : autoCutTarget;
  const cutTargetAngle = findAngle(angles, cutTargetKey);
  const existingCut = cutAtFrame(annotations, currentTime, fps);
  let cutBlockedReason: string | null = null;
  if (existingCut) cutBlockedReason = 'There’s already a cut on this frame. Edit it in the list, or move the playhead.';
  else if (!cutTargetAngle) cutBlockedReason = angles.length > 1 ? 'No other angle has footage here.' : null;
  else if (cutTargetAngle.angleKey === liveKey) cutBlockedReason = `${angleName(cutTargetAngle)} is already live here.`;
  else if (!footageNow(cutTargetAngle.angleKey)) cutBlockedReason = `${angleName(cutTargetAngle)} has no footage here.`;

  // ---- Actions -----------------------------------------------------------------
  const selectAngle = useCallback((angleKey: string) => {
    if (selectPlayerAngle(angleKey)) return;
    const angle = findAngle(angles, angleKey);
    if (angle) notify(`${angleName(angle)} has no footage at ${formatTimecode(getTime(), fps)}.`, 'info');
  }, [angles, fps, getTime, notify, selectPlayerAngle]);

  const focusComposer = useCallback((next: ComposerMode) => {
    if (!editable) return;
    if (next === 'cut' && angles.length < 2) return;
    pause();
    setMode(next);
    window.setTimeout(() => {
      (next === 'cut' ? cutInputRef.current : noteInputRef.current)?.focus();
    }, 0);
  }, [angles.length, editable, pause]);

  const markIn = useCallback(() => {
    const time = getTime();
    setInPoint(time);
    setOutPoint((out) => (out !== null && out <= time ? null : out));
  }, [getTime]);

  const markOut = useCallback(() => {
    const time = getTime();
    setOutPoint(time);
    setInPoint((current) => (current !== null && current >= time ? null : current));
  }, [getTime]);

  const clearMarks = useCallback(() => {
    setInPoint(null);
    setOutPoint(null);
    setLoopEnabled(false);
  }, []);

  /** Adds one annotation (its own request), then shows it selected. */
  const createAnnotation = useCallback(async (draft: AnnotationDraft, action: SaveAction): Promise<MulticamAnnotation | null> => {
    if (!multicamSetId || addingRef.current) return null;
    addingRef.current = true;
    setAdding(true);
    try {
      const { endTimeMs, ...rest } = toMutationInput(draft);
      const created = await runInOrder(() =>
        addMulticamAnnotation(multicamSetId, endTimeMs === null ? rest : { ...rest, endTimeMs })
      );
      const [saved] = normalizeAnnotations(created ? [created] : []);
      if (!saved) throw new Error('The saved annotation was not returned.');
      setAnnotations((previous) => sortAnnotations([...previous.filter((annotation) => annotation.id !== saved.id), saved]));
      setSelectedId(saved.id);
      return saved;
    } catch (err) {
      console.error('Failed to add multicam annotation:', err);
      notify(describeSaveError(err, action), 'error');
      return null;
    } finally {
      addingRef.current = false;
      setAdding(false);
    }
  }, [multicamSetId, notify, runInOrder]);

  /** Saves every field of one annotation; resolves with what the server stored. */
  const sendUpdate = useCallback(async (id: string, draft: AnnotationDraft): Promise<MulticamAnnotation | null> => {
    const input = toMutationInput(draft);
    // null clears a range: the API's endTimeMs is a nullable Float, stored as sent.
    const updated = await runInOrder(() => updateMulticamAnnotation(id, input));
    const [saved] = normalizeAnnotations(updated ? [updated] : []);
    return saved ?? null;
  }, [runInOrder]);

  const nextRequest = useCallback((id: string) => {
    const seq = (requestSeqRef.current[id] ?? 0) + 1;
    requestSeqRef.current[id] = seq;
    return seq;
  }, []);

  const applySaved = useCallback((id: string, seq: number, saved: MulticamAnnotation | null) => {
    // A live change queued since is newer, and is saved after this answer.
    if (!saved || requestSeqRef.current[id] !== seq || liveSession.isBusy(id)) return;
    setAnnotations((previous) => sortAnnotations(previous.map((annotation) => (annotation.id === id ? saved : annotation))));
  }, [liveSession]);

  const handleAddNote = async () => {
    const text = composerText.trim();
    if (!text || !editable) return;
    const limit = duration > 0 ? duration : Number.POSITIVE_INFINITY;
    const span = resolveNoteSpan(inPoint, outPoint, getTime(), 1 / fps);
    const time = Math.min(span.time, limit);
    const endTime = typeof span.endTime === 'number' ? Math.min(span.endTime, limit) : null;
    const scope = noteScope ?? viewKey;
    const saved = await createAnnotation({
      type: 'NOTE',
      time,
      endTime: endTime !== null && endTime > time ? endTime : null,
      text,
      category: composerCategory,
      targetAngleKey: !scope || scope === ALL_ANGLES ? null : scope,
    }, 'add-note');
    if (!saved) return;
    // Keep anything typed while it was saving.
    setComposerText((current) => (current.trim() === text ? '' : current));
    clearMarks();
    setNoteScope(null);
    notify('Note added');
  };

  const handleAddCut = async () => {
    if (!editable || !cutTargetAngle || cutBlockedReason) return;
    const target = cutTargetAngle.angleKey;
    const reason = cutReason.trim();
    const time = duration > 0 ? Math.min(getTime(), duration) : getTime();
    const saved = await createAnnotation({ type: 'SWITCH', time, endTime: null, text: reason, targetAngleKey: target }, 'add-cut');
    if (!saved) return;
    setCutReason((current) => (current.trim() === reason ? '' : current));
    setCutTarget(null);
    notify(`${cutLabel(target, angles)} added`);
    // Show what the cut shows from here on.
    if (viewKey !== target) selectPlayerAngle(target);
  };

  const handleSaveEdit = async (draft: AnnotationDraft) => {
    const id = editingId;
    if (!id) return;
    setEditSaving(true);
    setEditError(null);
    const seq = nextRequest(id);
    try {
      const saved = await sendUpdate(id, draft);
      applySaved(id, seq, saved);
      setEditingId(null);
      notify('Changes saved');
    } catch (err) {
      console.error('Failed to update multicam annotation:', err);
      setEditError(describeSaveError(err, 'update'));
    } finally {
      setEditSaving(false);
    }
  };

  /** A marker dragged or nudged on the timeline: shown at once, saved, put back on failure. */
  const handleTimelineChange = useCallback((item: TimelineItem, _lane: TimelineLane, next: TimelineItemChange) => {
    const original = annotationsRef.current.find((annotation) => annotation.id === item.id);
    if (!original || !editable) return Promise.reject(new Error('This annotation cannot be moved.'));
    if (liveSession.isBusy(original.id)) {
      notify(STILL_SAVING, 'info');
      return Promise.reject(new Error(STILL_SAVING));
    }
    const draft = draftFromAnnotation(original);
    draft.time = next.start;
    draft.endTime = isCut(original)
      ? (typeof draft.endTime === 'number' && draft.endTime > next.start ? draft.endTime : null)
      : (next.end !== null && next.end > next.start ? next.end : null);
    const moved: MulticamAnnotation = {
      ...original,
      timelineTimeMs: msFromSeconds(draft.time),
      endTimeMs: typeof draft.endTime === 'number' ? msFromSeconds(draft.endTime) : null,
    };
    setAnnotations((previous) => sortAnnotations(previous.map((annotation) => (annotation.id === original.id ? moved : annotation))));
    const seq = nextRequest(original.id);
    return sendUpdate(original.id, draft)
      .then((saved) => applySaved(original.id, seq, saved))
      .catch((err) => {
        console.error('Failed to move multicam annotation:', err);
        // A live change queued since saves the cut as it is shown, so it is not put back.
        if (requestSeqRef.current[original.id] === seq && !liveSession.isBusy(original.id)) {
          setAnnotations((previous) => sortAnnotations(previous.map((annotation) => (annotation.id === original.id ? original : annotation))));
        }
        notify(describeSaveError(err, 'move'), 'error');
        throw err;
      });
  }, [applySaved, editable, liveSession, nextRequest, notify, sendUpdate]);

  const handleConfirmDelete = async () => {
    const target = deleteId ? annotationById.get(deleteId) : undefined;
    if (!target) {
      setDeleteId(null);
      return;
    }
    setDeleting(true);
    setDeleteError(null);
    // A slower update still on its way must not bring it back.
    nextRequest(target.id);
    try {
      await runInOrder(() => deleteMulticamAnnotation(target.id));
      setAnnotations((previous) => previous.filter((annotation) => annotation.id !== target.id));
      setSelectedId((current) => (current === target.id ? null : current));
      setDeleteId(null);
      // A note (or a transcript mark) can be put back; cuts are redone in live cut.
      if (isCut(target)) notify('Cut deleted');
      else notify(markDeletedMessage({ kind: directionCategoryOf(target.category) ?? 'note' }), 'success', () => void restoreNote(target));
    } catch (err) {
      console.error('Failed to delete multicam annotation:', err);
      // The dialog stays open so it can be retried or cancelled.
      setDeleteError(describeSaveError(err, 'delete'));
    } finally {
      setDeleting(false);
    }
  };

  const handleMarkReady = async () => {
    if (!multicamSetId || statusBusy) return;
    setStatusBusy(true);
    try {
      const updated = await setMulticamStatus(multicamSetId, 'READY');
      setMulticamSet((previous) => (previous
        ? { ...previous, status: updated?.status ?? 'READY', updatedAt: updated?.updatedAt ?? previous.updatedAt }
        : previous));
      notify('Set marked as ready');
    } catch (err) {
      console.error('Failed to update multicam status:', err);
      notify(describeSaveError(err, 'status'), 'error');
    } finally {
      setStatusBusy(false);
    }
  };

  const openEdit = useCallback((id: string) => {
    if (!editable || !annotationsRef.current.some((annotation) => annotation.id === id)) return;
    // A cut made live is saved in order; edits wait until it is.
    if (liveSession.isBusy(id)) {
      notify(STILL_SAVING, 'info');
      return;
    }
    setEditError(null);
    setEditingId(id);
  }, [editable, liveSession, notify]);

  const openDelete = useCallback((id: string) => {
    if (!editable) return;
    if (liveSession.isBusy(id)) {
      notify(STILL_SAVING, 'info');
      return;
    }
    setDeleteError(null);
    setDeleteId(id);
  }, [editable, liveSession, notify]);

  /** Moves the playhead of the view in use. */
  const seekView = useCallback((seconds: number) => {
    if (liveMode) liveRef.current?.seek(seconds);
    else seek(seconds);
  }, [liveMode, seek]);

  /** The exact playhead of the view in use. */
  const getViewTime = useCallback(
    () => (liveMode ? liveRef.current?.getTime() ?? liveClock.time : getTime()),
    [getTime, liveClock.time, liveMode]
  );

  /**
   * Go to a moment, showing the angle that matters there: the angle a cut goes
   * to, or the angle a note is about (when it has footage there). Live cut
   * shows every angle, so it only moves the playhead.
   */
  const jumpTo = useCallback((seconds: number, angleKey?: string | null) => {
    if (liveMode) {
      liveRef.current?.seek(seconds);
      return;
    }
    seek(seconds);
    if (angleKey && angleKey !== viewKey && hasFootage(angleKey, seconds)) selectPlayerAngle(angleKey);
  }, [hasFootage, liveMode, seek, selectPlayerAngle, viewKey]);

  /**
   * Review or live cut, keeping the playhead and pausing. A switch the creator
   * makes is kept for the set; one the studio makes for them (to write a note)
   * is not.
   */
  const switchView = useCallback((next: StudioView, { remember = true }: { remember?: boolean } = {}) => {
    if (remember) storeView(multicamSetId, next);
    if (next === view || (next === 'live' && angles.length < 2)) return;
    liveSession.endTake();
    if (next === 'live') {
      const time = getTime();
      pause();
      setLiveStart(time);
      setLiveClock({ time, playing: false });
    } else {
      const time = liveRef.current?.getTime() ?? liveClock.time;
      liveRef.current?.pause();
      seek(time);
    }
    setChosenView(next);
  }, [angles.length, getTime, liveClock.time, liveSession, multicamSetId, pause, seek, view]);

  const handleLiveTime = useCallback((time: number, playing: boolean) => {
    setLiveClock((previous) => (previous.time === time && previous.playing === playing ? previous : { time, playing }));
  }, []);

  /**
   * A note on a transcript line: the note covers the sentence (in and out
   * marks), quotes it, and the composer opens ready for the instruction.
   */
  const handleAddNoteFromLine = useCallback((line: TranscriptLineAction) => {
    if (!editable) return;
    if (liveMode) switchView('review', { remember: false });
    seek(line.start);
    setInPoint(line.start);
    setOutPoint(line.end > line.start ? line.end : null);
    setComposerText((current) => (current.trim() ? current : `“${line.text}” — `));
    focusComposer('note');
  }, [editable, focusComposer, liveMode, seek, switchView]);

  // The transcript is of the set's sound (its separate recording, else its
  // sound angle), placed by its sync.
  const transcriptAngle = useMemo(
    () => transcriptSourceOf(
      separateAudioSource,
      findAngle(angles, multicamSet?.audioAngleKey) ?? findAngle(angles, masterKey) ?? null
    ),
    [separateAudioSource, angles, multicamSet?.audioAngleKey, masterKey]
  );
  const transcriptPlacement = transcriptAngle ? transcriptAngle.placement : null;

  // Remove, Short and Note from the transcript's words (see useMulticamTranscriptMarks).
  const { marking: transcriptMarking, restore: restoreNote } = useMulticamTranscriptMarks({
    multicamSetId,
    annotations,
    angles,
    locked,
    editable,
    notesStatus,
    selectedId,
    setSelectedId,
    setAnnotations,
    runInOrder,
    nextRequest,
    notify,
    jumpTo,
    openEdit,
  });

  const handleSeekToAnnotation = useCallback((item: StudioAnnotation) => {
    jumpTo(item.time, annotationById.get(item.id)?.targetAngleKey);
    setSelectedId(item.id);
  }, [annotationById, jumpTo]);

  const handleTimelineSelect = useCallback((item: TimelineItem) => {
    const id = annotationIdOf(item);
    setSelectedId(id);
    if (item.id === 'segment:start') {
      jumpTo(item.start, masterKey);
      return;
    }
    if (id) jumpTo(item.start, annotationById.get(id)?.targetAngleKey);
  }, [annotationById, jumpTo, masterKey]);

  const handleTimelineActivate = useCallback((item: TimelineItem) => {
    const id = annotationIdOf(item);
    if (id) openEdit(id);
  }, [openEdit]);

  // ---- Keyboard shortcuts (bound once, read through a ref) ---------------------
  const anyDialogOpen = helpOpen || Boolean(editingId) || Boolean(deleteId);
  const shortcutRef = useRef<(event: KeyboardEvent) => void>(() => undefined);

  /** Keys in live cut: the numbers cut; the transport keys drive the live view. */
  const handleLiveKey = (event: KeyboardEvent, live: LiveSwitcherHandle) => {
    const target = event.target as HTMLElement | null;
    const nativeControl = target && ['BUTTON', 'A'].includes(target.tagName);

    const digit = /^(?:Digit|Numpad)([1-9])$/.exec(event.code);
    if (digit) {
      const angle = angles[Number(digit[1]) - 1];
      if (!angle) return;
      event.preventDefault();
      // A held key repeats; one press is one cut.
      if (!event.repeat) live.press(angle.angleKey);
      return;
    }

    switch (event.code) {
      case 'Space':
        if (nativeControl) return;
        event.preventDefault();
        live.toggle();
        break;
      case 'KeyK':
        event.preventDefault();
        live.pause();
        break;
      case 'KeyL':
        // Every angle plays at normal speed here, so L plays rather than shuttles.
        if (event.shiftKey) return;
        event.preventDefault();
        if (!live.isPlaying()) live.play();
        break;
      case 'ArrowLeft':
        event.preventDefault();
        live.skip(event.shiftKey ? -10 : -5);
        break;
      case 'ArrowRight':
        event.preventDefault();
        live.skip(event.shiftKey ? 10 : 5);
        break;
      case 'ArrowUp':
        event.preventDefault();
        player.setVolume(player.volume + 0.1);
        break;
      case 'ArrowDown':
        event.preventDefault();
        player.setVolume(player.volume - 0.1);
        break;
      case 'Comma':
        event.preventDefault();
        live.stepFrames(-1);
        break;
      case 'Period':
        event.preventDefault();
        live.stepFrames(1);
        break;
      case 'Home':
        event.preventDefault();
        live.seek(0);
        break;
      case 'End':
        event.preventDefault();
        live.seek(duration);
        break;
      case 'KeyM':
        event.preventDefault();
        player.toggleMute();
        break;
      case 'KeyF':
        event.preventDefault();
        fullscreen.toggle();
        break;
      case 'Escape':
        clearMarks();
        setSelectedId(null);
        break;
      case 'BracketLeft':
      case 'BracketRight': {
        event.preventDefault();
        const next = adjacentAnnotationTime(panelAnnotations, live.getTime(), event.code === 'BracketRight' ? 1 : -1);
        if (next !== null) {
          const match = panelAnnotations.find((annotation) => annotation.time === next);
          live.seek(next);
          if (match) setSelectedId(match.id);
        }
        break;
      }
      case 'Equal':
      case 'NumpadAdd':
        event.preventDefault();
        timelineRef.current?.zoomIn();
        break;
      case 'Minus':
      case 'NumpadSubtract':
        event.preventDefault();
        timelineRef.current?.zoomOut();
        break;
      case 'Backslash':
        event.preventDefault();
        timelineRef.current?.fit();
        break;
      default:
        break;
    }
  };

  shortcutRef.current = (event: KeyboardEvent) => {
    if (setStatus !== 'ready' || angles.length === 0) return;
    if (event.defaultPrevented || anyDialogOpen || isTypingTarget(event.target)) return;

    // Ctrl/⌘ + Z undoes the last live change; it means nothing in review.
    const undoKey = (event.ctrlKey || event.metaKey) && !event.altKey && !event.shiftKey
      && (event.key || '').toLowerCase() === 'z';
    if (undoKey) {
      if (!liveMode || !liveRef.current) return;
      event.preventDefault();
      liveRef.current.undo();
      return;
    }
    if (event.ctrlKey || event.metaKey || event.altKey) return;

    if (event.key === '?') {
      event.preventDefault();
      setHelpOpen(true);
      return;
    }
    if (liveMode) {
      if (liveRef.current) handleLiveKey(event, liveRef.current);
      return;
    }
    if (!player.element) return;
    const target = event.target as HTMLElement | null;
    const nativeControl = target && ['BUTTON', 'A'].includes(target.tagName);

    const digit = /^(?:Digit|Numpad)([1-9])$/.exec(event.code);
    if (digit) {
      const angle = angles[Number(digit[1]) - 1];
      if (angle) {
        event.preventDefault();
        selectAngle(angle.angleKey);
      }
      return;
    }

    switch (event.code) {
      case 'Space':
        if (nativeControl) return;
        event.preventDefault();
        player.toggle();
        break;
      case 'KeyK':
        event.preventDefault();
        player.pause();
        break;
      case 'KeyJ':
        event.preventDefault();
        player.shuttleReverse();
        break;
      case 'KeyL':
        event.preventDefault();
        if (event.shiftKey) {
          if (inPoint !== null && outPoint !== null) setLoopEnabled((value) => !value);
        } else {
          player.shuttleForward();
        }
        break;
      case 'ArrowLeft':
        event.preventDefault();
        player.skip(event.shiftKey ? -10 : -5);
        break;
      case 'ArrowRight':
        event.preventDefault();
        player.skip(event.shiftKey ? 10 : 5);
        break;
      case 'ArrowUp':
        event.preventDefault();
        player.setVolume(player.volume + 0.1);
        break;
      case 'ArrowDown':
        event.preventDefault();
        player.setVolume(player.volume - 0.1);
        break;
      case 'Comma':
        event.preventDefault();
        player.stepFrames(-1);
        break;
      case 'Period':
        event.preventDefault();
        player.stepFrames(1);
        break;
      case 'Home':
        event.preventDefault();
        player.seek(0);
        break;
      case 'End':
        event.preventDefault();
        player.seek(duration);
        break;
      case 'KeyM':
        event.preventDefault();
        player.toggleMute();
        break;
      case 'KeyF':
        event.preventDefault();
        fullscreen.toggle();
        break;
      case 'KeyA':
      case 'Insert':
        event.preventDefault();
        focusComposer('note');
        break;
      case 'KeyC':
        event.preventDefault();
        focusComposer('cut');
        break;
      case 'KeyI':
        event.preventDefault();
        markIn();
        break;
      case 'KeyO':
        event.preventDefault();
        markOut();
        break;
      case 'Escape':
        clearMarks();
        setSelectedId(null);
        break;
      case 'BracketLeft':
      case 'BracketRight': {
        event.preventDefault();
        const next = adjacentAnnotationTime(panelAnnotations, player.getTime(), event.code === 'BracketRight' ? 1 : -1);
        if (next !== null) {
          const match = panelAnnotations.find((annotation) => annotation.time === next);
          jumpTo(next, match ? annotationById.get(match.id)?.targetAngleKey : null);
          if (match) setSelectedId(match.id);
        }
        break;
      }
      case 'Equal':
      case 'NumpadAdd':
        event.preventDefault();
        timelineRef.current?.zoomIn();
        break;
      case 'Minus':
      case 'NumpadSubtract':
        event.preventDefault();
        timelineRef.current?.zoomOut();
        break;
      case 'Backslash':
        event.preventDefault();
        timelineRef.current?.fit();
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    const listener = (event: KeyboardEvent) => shortcutRef.current(event);
    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  }, []);

  // ---- Loading and load failure ------------------------------------------------
  if (setStatus !== 'ready' || !multicamSet) {
    const copy = LOAD_ERROR_COPY[loadProblem];
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: brand.canvas }}>
        <Box sx={{ maxWidth: 1680, mx: 'auto', px: { xs: 2, md: 3 }, pt: { xs: 2, md: 2.5 }, pb: 6 }}>
          <MulticamStudioHeader title={setStatus === 'error' ? 'Multicam set' : <Skeleton width={280} aria-hidden="true" />} />
          {setStatus === 'error' ? (
            <Alert
              severity="error"
              action={(
                <Button color="inherit" size="small" onClick={() => setSetNonce((value) => value + 1)}>
                  Retry
                </Button>
              )}
              sx={{ maxWidth: 720 }}
            >
              <AlertTitle>{copy.title}</AlertTitle>
              {copy.body}
            </Alert>
          ) : (
            <LoadingState variant="detail" label="Loading the multicam set" />
          )}
        </Box>
      </Box>
    );
  }

  const draft = multicamSet.status === 'DRAFT';
  const noteScopeValue = noteScope ?? (viewKey || ALL_ANGLES);
  // "No sound here" only when nothing can be heard: where the set's sound has
  // no footage, the angle on screen (or the next with footage) is heard instead.
  const audioMissingHere = Boolean(player.separateAudio && audioAngle && player.heardKeyAt(currentTime) === null);
  // AI transcripts are Pro Max; an unknown plan (older API) holds nothing back.
  const transcriptsLocked = multicamSet.ownerFeatures?.transcripts === false;
  // Joined clips (end to end) are each heard in turn, not the audio angle throughout.
  const joined = playsInSequence(angles);
  const eachClipHeard = joined && !recording;
  const editingAnnotation = editingId ? annotationById.get(editingId) ?? null : null;
  const deleteTarget = deleteId ? annotationById.get(deleteId) ?? null : null;
  const savingStatus = savesInFlight > 0 ? 'saving' : lastSavedAt ? 'saved' : 'idle';
  let liveReadOnlyReason: string | null = null;
  if (locked) liveReadOnlyReason = 'This set was submitted for editing, so its cuts can’t be changed.';
  else if (notesStatus === 'loading') liveReadOnlyReason = 'You can cut once this set’s cuts and notes have loaded.';
  else if (notesStatus === 'error') liveReadOnlyReason = 'Cuts can’t be changed until this set’s cuts and notes load. Use Retry above.';

  const header = (
    <MulticamStudioHeader
      title={multicamSet.name}
      status={multicamSet.status}
      chips={(
        <>
          {duration > 0 && (
            <Chip
              size="small"
              label={formatTimecode(duration, fps)}
              aria-label={`Length ${formatTimecode(duration, fps)}`}
              sx={{ height: 24, fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}
            />
          )}
          <FrameRateChip
            fps={fps}
            known={false}
            chosen={fpsOverride !== null}
            onChange={(rate) => {
              setFpsOverride(rate);
              storeFps(multicamSetId, rate);
            }}
          />
        </>
      )}
      meta={angles.length > 0 ? (
        <>
          <span data-testid="studio-angle-count">{joined ? `${pluralize(angles.length, 'clip')}, joined` : pluralize(angles.length, 'angle')}</span>
          {audioAngle && (
            <>
              <span aria-hidden="true">·</span>
              <span data-testid="audio-source-label">
                {eachClipHeard
                  ? 'Each clip’s own sound'
                  : `Sound from ${audioAngle.angleKey === RECORDING_KEY ? recordingDisplayName(audioAngle) : angleName(audioAngle)}`}
              </span>
            </>
          )}
        </>
      ) : undefined}
      actions={(
        <>
          <SaveStatus
            status={savingStatus}
            lastSavedAt={lastSavedAt}
            hasUnsavedChanges={false}
            onSaveNow={() => undefined}
            blocked={notesStatus === 'error'}
          />
          {draft && (
            <Button
              variant="outlined"
              onClick={() => void handleMarkReady()}
              disabled={statusBusy}
              startIcon={statusBusy ? <CircularProgress size={16} /> : <TaskAltOutlinedIcon />}
            >
              Mark as ready
            </Button>
          )}
          {!locked && (
            <Button
              variant="outlined"
              startIcon={<SendOutlinedIcon />}
              onClick={() => navigate(`/submit-editing-request?multicamSetId=${encodeURIComponent(multicamSet.id)}`)}
            >
              Submit for editing
            </Button>
          )}
          {/* describeChild: the visible "Help" stays the button's name; the tooltip describes it. */}
          <Tooltip title="How the studio works, and keyboard shortcuts (?)" describeChild>
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<HelpOutlineOutlinedIcon />}
              onClick={() => setHelpOpen(true)}
              sx={{ borderColor: brand.border, bgcolor: 'background.paper' }}
            >
              Help
            </Button>
          </Tooltip>
        </>
      )}
    />
  );

  if (angles.length === 0) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: brand.canvas }}>
        <Box sx={{ maxWidth: 1680, mx: 'auto', px: { xs: 2, md: 3 }, pt: { xs: 2, md: 2.5 }, pb: 6 }}>
          {header}
          <EmptyState
            icon={<VideocamOutlinedIcon />}
            title="This set has no angles yet"
            description="Add at least one video to the set to start marking cuts and notes."
            actionLabel={locked ? undefined : 'Edit set-up'}
            onAction={locked ? undefined : () => navigate(`/multicam/${multicamSet.id}/edit`)}
            secondaryActionLabel="Back to multicam sets"
            onSecondaryAction={() => navigate(MULTICAM_LIBRARY_PATH)}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: brand.canvas }}>
      <Box sx={{ maxWidth: 1680, mx: 'auto', px: { xs: 2, md: 3 }, pt: { xs: 2, md: 2.5 }, pb: 6 }}>
        {header}

        {locked && (
          <Alert severity="info" icon={<LockOutlinedIcon />} sx={{ mb: 2 }}>
            This set was submitted for editing, so its cuts and notes can’t be changed. You can still play it back.
          </Alert>
        )}

        {notesStatus === 'error' && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={(
              <Button color="inherit" size="small" onClick={() => setNotesNonce((value) => value + 1)}>
                Retry
              </Button>
            )}
          >
            We couldn’t load this set’s cuts and notes. Retry before adding more, so nothing is added twice.
          </Alert>
        )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1fr) 380px', xl: 'minmax(0, 1fr) 420px' },
            gap: 2.5,
            alignItems: 'start',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
            {angles.length > 1 && (
              <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1.5, rowGap: 1, flexWrap: 'wrap' }}>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={liveMode ? 'live' : 'review'}
                  onChange={(_, next: StudioView | null) => next && switchView(next)}
                  aria-label="Studio view"
                  sx={{ height: 36, bgcolor: 'background.paper' }}
                >
                  <ToggleButton value="review" onMouseDown={keepFocus} sx={{ gap: 0.75, px: 1.5, fontWeight: 600 }}>
                    <VideocamOutlinedIcon sx={{ fontSize: 18 }} />
                    Review
                  </ToggleButton>
                  <ToggleButton value="live" onMouseDown={keepFocus} sx={{ gap: 0.75, px: 1.5, fontWeight: 600 }}>
                    <GridViewOutlinedIcon sx={{ fontSize: 18 }} />
                    Live cut
                  </ToggleButton>
                </ToggleButtonGroup>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 0 }}>
                  {liveMode
                    ? `Press ${liveKeysText(angles.length)}, or tap an angle, to cut to it as it plays. Play a stretch again to redo it.`
                    : 'Live cut plays every angle at once, so you can cut as it plays.'}
                </Typography>
              </Box>
            )}

            {/* Viewer */}
            <Box
              ref={fullscreen.ref}
              data-testid="multicam-viewer"
              data-view={liveMode ? 'live' : 'review'}
              sx={{
                position: 'relative',
                borderRadius: fullscreen.isFullscreen ? 0 : 3,
                overflow: 'hidden',
                bgcolor: playerTokens.stage,
                display: 'flex',
                flexDirection: 'column',
                ...(fullscreen.isFullscreen ? { height: '100vh' } : {}),
              }}
            >
              {/* Review stays mounted (and paused) under live cut, so coming back is instant. */}
              <Box
                sx={{
                  display: liveMode ? 'none' : 'flex',
                  flexDirection: 'column',
                  flex: fullscreen.isFullscreen ? 1 : 'none',
                  minHeight: 0,
                }}
              >
                <VideoStage
                  videoUrl={viewAngle?.videoUrl || undefined}
                  player={player}
                  testId="multicam-video-player"
                  notes={stageNotes}
                  fullscreen={fullscreen.isFullscreen}
                  maxHeight="min(50vh, 620px)"
                  badge={viewAngle ? (
                    <ViewerBadge
                      angle={viewAngle}
                      liveKey={liveKey}
                      fps={fps}
                      inPoint={inPoint}
                      outPoint={outPoint}
                      silentAudioAngle={audioMissingHere ? audioAngle : null}
                      silentAudioText={audioMissingHere && audioAngle?.angleKey === RECORDING_KEY
                        ? `No sound here: ${recordingPhrase(audioAngle)} doesn’t cover this part`
                        : undefined}
                    />
                  ) : undefined}
                  placeholder={(
                    <Typography variant="body2" sx={{ color: playerTokens.textMuted, px: 3, textAlign: 'center' }}>
                      This angle has no video file. Choose another angle.
                    </Typography>
                  )}
                />
                {/* The audio angle, heard while another angle is on screen. Only
                    fetched in full while it is actually needed. */}
                {audioAngle?.videoUrl && (angles.length > 1 || player.separateAudio) && (
                  // eslint-disable-next-line jsx-a11y/media-has-caption
                  <audio
                    ref={player.audioRef}
                    src={audioAngle.videoUrl}
                    preload={player.separateAudio ? 'auto' : 'metadata'}
                    data-testid="multicam-audio-source"
                    style={{ display: 'none' }}
                  />
                )}
                <TransportBar
                  player={player}
                  fps={fps}
                  disabled={!viewAngle?.videoUrl}
                  markers={scrubMarkers}
                  inPoint={inPoint}
                  outPoint={outPoint}
                  loop={{
                    enabled: loopEnabled,
                    available: inPoint !== null && outPoint !== null && outPoint > inPoint,
                    onToggle: () => setLoopEnabled((value) => !value),
                  }}
                  isFullscreen={fullscreen.isFullscreen}
                  onToggleFullscreen={fullscreen.toggle}
                  extra={angles.length > 1 ? (
                    <AngleSwitcher
                      angles={angles}
                      viewKey={viewKey}
                      liveKey={liveKey}
                      hasFootage={footageNow}
                      onSelect={selectAngle}
                    />
                  ) : undefined}
                />
              </Box>

              {liveMode && (
                <MulticamLiveSwitcher
                  ref={liveRef}
                  angles={measuredAngles}
                  audioAngleKey={multicamSet.audioAngleKey}
                  audioSource={recording}
                  annotations={annotations}
                  masterKey={masterKey}
                  fps={fps}
                  initialTime={liveStart}
                  muted={player.muted}
                  volume={player.volume}
                  onToggleMute={player.toggleMute}
                  readOnlyReason={liveReadOnlyReason}
                  onPress={liveSession.press}
                  onUndo={liveSession.undo}
                  canUndo={editable && liveSession.undoDepth() > 0}
                  onTimeChange={handleLiveTime}
                  onTakeBoundary={liveSession.endTake}
                  isFullscreen={fullscreen.isFullscreen}
                  onToggleFullscreen={fullscreen.toggle}
                />
              )}
            </Box>

            {liveMode ? (
              <Box
                data-testid="live-cut-help"
                sx={{ border: `1px solid ${brand.border}`, borderRadius: 2, bgcolor: 'background.paper', px: 2, py: 1.5 }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {liveReadOnlyReason ? 'Playback only' : 'Cut as it plays'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  {liveReadOnlyReason ?? (
                    `Press ${cutKeysLabel(angles.length)}, or tap an angle, to cut to it on the frame that’s showing. `
                    + 'Playing a stretch again replaces the cuts between your presses. '
                    + `${undoShortcutLabel()} undoes the last change.`
                  )}
                </Typography>
                {!liveReadOnlyReason && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }} data-testid="live-cut-fix-help">
                    For an exact frame, pause, step with the , and . keys, and press the number. To fix a cut, click
                    it in the list and choose the pencil to change its time or camera, or drag it along the
                    timeline’s Cuts lane.
                  </Typography>
                )}
              </Box>
            ) : (
              <MulticamComposer
                mode={mode}
                onModeChange={setMode}
                angles={angles}
                noteScope={noteScopeValue}
                onNoteScopeChange={(scope) => setNoteScope(scope)}
                disabled={!editable}
                note={{
                  fps,
                  playheadTime: currentTime,
                  inPoint,
                  outPoint,
                  category: composerCategory,
                  onCategoryChange: setComposerCategory,
                  text: composerText,
                  onTextChange: setComposerText,
                  onSubmit: () => void handleAddNote(),
                  onMarkIn: markIn,
                  onMarkOut: markOut,
                  onClearIn: () => {
                    setInPoint(null);
                    setLoopEnabled(false);
                  },
                  onClearOut: () => {
                    setOutPoint(null);
                    setLoopEnabled(false);
                  },
                  onFocus: () => player.pause(),
                  disabled: !editable || adding,
                  inputRef: noteInputRef,
                }}
                cut={{
                  fps,
                  playheadTime: currentTime,
                  angles,
                  target: cutTargetKey,
                  onTargetChange: setCutTarget,
                  hasFootage: footageNow,
                  reason: cutReason,
                  onReasonChange: setCutReason,
                  onSubmit: () => void handleAddCut(),
                  blockedReason: editable ? cutBlockedReason : null,
                  disabled: !editable,
                  busy: adding,
                  onFocus: () => player.pause(),
                  inputRef: cutInputRef,
                }}
              />
            )}

            {angles.length > 1 && !locked && (
              <SuggestCutsPanel
                upgradeRequired={multicamSet.ownerFeatures?.suggestCuts === false}
                multicamSetId={multicamSet.id}
                angles={angles}
                masterKey={masterKey}
                duration={duration}
                hasFootage={hasFootage}
                annotations={annotations}
                editable={editable}
                speakers={speakers}
                runInOrder={runInOrder}
                onAnnotationsChange={updateAnnotations}
                onPreviewLane={setSuggestionLane}
                onBatchSaved={liveSession.resetUndo}
                notify={notify}
              />
            )}

            <ProTimeline
              ref={timelineRef}
              title="Timeline"
              duration={duration}
              currentTime={playheadTime}
              playing={isPlaying}
              fps={fps}
              lanes={suggestionLane ? [...lanes, suggestionLane] : lanes}
              selectedItemId={selectedId}
              inPoint={inPoint}
              outPoint={outPoint}
              onSeek={seekView}
              onItemSelect={handleTimelineSelect}
              onItemActivate={handleTimelineActivate}
              onItemChange={editable ? handleTimelineChange : undefined}
              emptyMessage="The timeline appears once the angles’ lengths are known."
              data-testid="multicam-timeline"
            />
          </Box>

          {/* Side panels: the cuts and notes list first; further panels stack below it. */}
          <Box
            data-testid="multicam-side-panels"
            sx={{ position: { lg: 'sticky' }, top: { lg: 16 }, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            <NotesTranscriptSwitch
              notesLabel="Cuts and notes"
              notes={(
                <AnnotationPanel
                  annotations={panelAnnotations}
                  fps={fps}
                  activeIds={activeIds}
                  selectedId={selectedId}
                  playing={isPlaying}
                  readOnly={!editable}
                  title="Cuts and notes"
                  // Cuts are stored as scene changes; name them as what they are.
                  getCategoryLabel={(annotation) => (annotationById.get(annotation.id)?.type === 'SWITCH' ? 'Camera cut' : undefined)}
                  onSeek={handleSeekToAnnotation}
                  onEdit={openEdit}
                  onDelete={openDelete}
                  emptyTitle={notesStatus === 'loading' ? 'Loading cuts and notes…' : 'No cuts or notes yet'}
                  emptyHint={notesStatus === 'loading'
                    ? 'One moment.'
                    : locked
                      ? 'None were added to this set.'
                      : 'Pick shots in Live cut as it plays, let Suggest cuts pick them from who’s speaking, or pause and add a camera cut (C) or a note (A) below the video. Help explains each step.'}
                  renderExtra={(item) => {
                    const annotation = annotationById.get(item.id);
                    if (!annotation || isCut(annotation) || !annotation.targetAngleKey) return null;
                    const angle = findAngle(angles, annotation.targetAngleKey);
                    return (
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, mt: 0.5, color: 'text.secondary', fontWeight: 600 }}
                      >
                        <Box component="span" aria-hidden="true" sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: angleColor(annotation.targetAngleKey) }} />
                        {angle ? `About ${angleName(angle)}` : `About angle ${annotation.targetAngleKey}`}
                      </Typography>
                    );
                  }}
                  listMaxHeight={transcriptAngle?.videoId ? 'calc(100vh - 380px)' : 'calc(100vh - 330px)'}
                  testId="multicam-annotation-panel"
                />
              )}
              transcript={transcriptAngle?.videoId ? (
                <TranscriptPanel
                  videoId={transcriptAngle.videoId}
                  placement={transcriptPlacement}
                  currentTime={playheadTime}
                  playing={isPlaying}
                  fps={fps}
                  onSeek={seekView}
                  autoStart={!transcriptsLocked}
                  canStart={!transcriptsLocked}
                  upgrade={transcriptsLocked ? {
                    title: 'Transcripts come with Pro Max',
                    description: 'Every line, time-coded and named by speaker. Select words to remove them or make a short, and your editor gets them with the cut.',
                  } : null}
                  exportName={multicamSet.name}
                  onAddNote={editable ? handleAddNoteFromLine : undefined}
                  speakerAt={speakers.speakerAt}
                  marking={transcriptMarking}
                  listMaxHeight="calc(100vh - 380px)"
                  testId="multicam-transcript-panel"
                />
              ) : null}
            />
          </Box>
        </Box>
      </Box>

      <MulticamAnnotationDialog
        open={Boolean(editingAnnotation)}
        annotation={editingAnnotation}
        angles={angles}
        fps={fps}
        duration={duration}
        getPlayhead={getViewTime}
        hasFootage={hasFootage}
        saving={editSaving}
        error={editError}
        onCancel={() => {
          if (!editSaving) setEditingId(null);
        }}
        onSave={(next) => void handleSaveEdit(next)}
      />

      <ShortcutHelpDialog
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        groups={MULTICAM_SHORTCUTS}
        title="Studio help"
        intro={<MulticamStudioGuide />}
      />

      <DeleteAnnotationDialog
        annotation={deleteTarget}
        undoable={Boolean(deleteTarget && !isCut(deleteTarget))}
        angles={angles}
        fps={fps}
        deleting={deleting}
        error={deleteError}
        onCancel={() => setDeleteId(null)}
        onConfirm={() => void handleConfirmDelete()}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === 'error' || snackbar.undo ? 6000 : 4000}
        onClose={(_, reason) => {
          if (reason === 'clickaway') return;
          setSnackbar((previous) => ({ ...previous, open: false }));
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={() => setSnackbar((previous) => ({ ...previous, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          action={snackbar.undo ? (
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                snackbar.undo?.();
                setSnackbar((previous) => ({ ...previous, open: false, undo: undefined }));
              }}
              sx={{ fontWeight: 700 }}
            >
              Undo
            </Button>
          ) : undefined}
          sx={{ alignItems: 'center' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

/**
 * One studio per set: moving from one set's page to another's starts fresh
 * (player, frame rate, composer) instead of carrying the last set's state over.
 */
const AnnotateMulticamRoute: React.FC = () => {
  const { multicamSetId } = useParams<{ multicamSetId: string }>();
  return <AnnotateMulticam key={multicamSetId} />;
};

export default AnnotateMulticamRoute;
